
import {of as observableOf,  Observable } from 'rxjs';

import { exhaustMap, catchError, map, share, throttleTime, switchMap, debounceTime, filter, withLatestFrom } from 'rxjs/operators';
import { Instrument } from './../../models/quote/instrument.model';
import { StocksExchangeInvestment } from './../../models/exchange/stocks-exchange-investment.model';
import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { StocksExchangeWalletAction } from './stocks-exchange-wallet.action';
import { BackendService } from '../../services/backend/backend.service';
import { RoutesTable } from '../../resources/routes-table.model';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { Store } from '@ngrx/store';
import { AppState } from '../../interfaces/app-state.interface';
import { RealtimeService } from '../../services/realtime/realtime.service';
import { IApiQueryListResponse } from '../../interfaces/api/api-query-list-response/api-query-list-response.interface';
import { BackendToFrontendParserService } from '../../services/parser/backend-to-frontend-parser.service';
import { PosRiskAction } from '../posrisk/posrisk.action';
import { Order } from '../../models/exchange/order.model';
import { CustodyClientOrder } from '../../models/custody/custody-client-order.model';
import { CustodyStructuredProduct } from '../../models/custody/custody-structured-product.model';
import { StocksMarketAction } from '../stocks-market/stocks-market.action';
import { Strategy } from '../../models/exchange/strategy.model';
import { Stress } from '../../models/pos-risk/stress.model';
import { InstrumentLite } from '../../models/quote/instrument-lite.model';
import { UtilitiesService } from '../../services/utilities.service';
import { RealtimeHub } from '../../enum/realtime/realtime-hub.enum';
import { SentryService } from '../../services/sentry/sentry.service';
import { ExchangeOrderStatus } from '../../enum/orders/order-status.enum';
import { StructuredProductsAction } from '../structured-products/structured-products.action';
import { InvestmentTypeEnum } from '../../enum/orders/investment-type.enum';

@Injectable()
export class StocksExchangeWalletEffects {

    /**
     * Busca as ordens de um cliente
     */
    @Effect()
    public getOrders$: Observable<StocksExchangeWalletAction> = this.actions$
        .pipe(
			ofType(StocksExchangeWalletAction.getOrders),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            debounceTime(300),
            switchMap(([action, auth]) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.exchange.routes.getClientOrders.uri)
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return { type: StocksExchangeWalletAction.getOrdersSuccess, payload: result.value.filter((i) => i) };
                            }
                            return { type: StocksExchangeWalletAction.getOrdersError, payload: result.value };
                        }),
                        catchError((error) => {
                            return observableOf({ type: StocksExchangeWalletAction.getOrdersError, payload: error });
                        }),
                    );
            }),
            share(),
        );

    /**
     * Busca apenas as ordens válidas de um cliente
     */
    @Effect()
    public getValidOrders$: Observable<StocksExchangeWalletAction> = this.actions$
        .pipe(
			ofType(StocksExchangeWalletAction.getValidOrders),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            debounceTime(300),
            switchMap(([action, auth]) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.exchange.routes.getClientValidOrders.uri)
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return { type: StocksExchangeWalletAction.getValidOrdersSuccess, payload: result.value.filter((i) => i) };
                            }
                            return { type: StocksExchangeWalletAction.getValidOrdersError, payload: result.value };
                        }),
                        catchError((error) => {
                            return observableOf({ type: StocksExchangeWalletAction.getValidOrdersError, payload: error });
                        }),
                    );
            }),
            share(),
        );

    @Effect({ dispatch: false })
    public validOrdersLoaded$ = this.actions$
        .pipe(
			ofType(StocksExchangeWalletAction.getValidOrdersSuccess),
			share()
		);

    /**
     * Busca uma ordem de um cliente
     */
    @Effect()
    public getOrder$: Observable<StocksExchangeWalletAction> = this.actions$
        .pipe(
			ofType(StocksExchangeWalletAction.getOrder),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
                return this.backendService
                    .get<DefaultResponse>(RoutesTable.composeUri(RoutesTable.exchange.routes.getClientOrder, action.payload))
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return { type: StocksExchangeWalletAction.getOrderSuccess, payload: result.value };
                            }
                            return { type: StocksExchangeWalletAction.getOrderError, payload: result.value };
                        }),
                        catchError((error) => {
                            return observableOf({ type: StocksExchangeWalletAction.getOrderError, payload: error });
                        }),
                    );
            }),
            share(),
        );

    /**
     * Cancela uma ordem de um cliente
     */
    @Effect()
    public closeOrder$: Observable<StocksExchangeWalletAction> = this.actions$
        .pipe(
			ofType(StocksExchangeWalletAction.closeOrder),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
                return this.backendService.post<DefaultResponse>(RoutesTable.exchange.routes.closeOrder.uri, action.payload)
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return { type: StocksExchangeWalletAction.getOrderSuccess, payload: action.payload.orderId };
                            }
                            return { type: StocksExchangeWalletAction.getOrderError, payload: result.value };
                        }),
                        catchError((error) => {
                            return observableOf({ type: StocksExchangeWalletAction.getOrderError, payload: error });
                        }),
                    );
            }),
            share(),
        );

    /**
     * Busca uma estrategia de um cliente
     */
    @Effect()
    public getStrategy$: Observable<StocksExchangeWalletAction> = this.actions$
        .pipe(
			ofType(StocksExchangeWalletAction.getStrategy),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
                return this.backendService
                    .get<DefaultResponse>(RoutesTable.composeUri(RoutesTable.exchange.routes.getClientStrategy, action.payload))
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                if (!result.value) {
                                    return { type: StocksExchangeWalletAction.strategyNotFound, payload: false};
                                }

                                return { type: StocksExchangeWalletAction.getStrategySuccess, payload: result.value };
                            }
                            return { type: StocksExchangeWalletAction.getStrategyError, payload: result.value };
                        }),
                        catchError((error) => {
                            return observableOf({ type: StocksExchangeWalletAction.getStrategyError, payload: error });
                        }),
                    );
            }),
            share(),
        );

    /**
     * Cancela uma estratégia de um cliente
     */
    @Effect()
    public closeStrategy$: Observable<StocksExchangeWalletAction> = this.actions$
        .pipe(
			ofType(StocksExchangeWalletAction.closeStrategy),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
            return this.backendService.post<DefaultResponse>(RoutesTable.exchange.routes.closeStrategy.uri, action.payload)
                .pipe(
                    map((result: any) => {
                        if (result.ok) {
                            return { type: StocksExchangeWalletAction.closeStrategySuccess, payload: action.payload.strategyId };
                        }
                        return { type: StocksExchangeWalletAction.closeStrategyError, payload: result.value };
                    }),
                    catchError((error) => {
                        return observableOf({ type: StocksExchangeWalletAction.closeStrategyError, payload: error });
                    }),
                );
            }),
            share(),
        );

    /**
     * Busca as "estratégias" de um cliente
     */
    @Effect()
    public getStrategies$: Observable<StocksExchangeWalletAction> = this.actions$
        .pipe(
			ofType(StocksExchangeWalletAction.getStrategies),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            debounceTime(300),
            switchMap((action) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.exchange.routes.getClientStrategies.uri)
                .pipe(
                    map((result: any) => {
                        if (result.ok) return { type: StocksExchangeWalletAction.getStrategiesSuccess, payload: result.value };
                        return { type: StocksExchangeWalletAction.getStrategiesError, payload: result.value };
                    }),
                    catchError((error) => {
                        return observableOf({ type: StocksExchangeWalletAction.getStrategiesError, payload: error });
                    }),
                );
            }),
            share(),
        );

    /**
     * Busca apenas as "estratégias" válidas de um cliente
     */
    @Effect()
    public getValidStrategies$: Observable<StocksExchangeWalletAction> = this.actions$
        .pipe(
			ofType(StocksExchangeWalletAction.getValidStrategies),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            debounceTime(300),
            exhaustMap((action) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.exchange.routes.getClientValidStrategies.uri)
                .pipe(
                    map((result: any) => {
                        if (result.ok) return { type: StocksExchangeWalletAction.getValidStrategiesSuccess, payload: result.value };
                        return { type: StocksExchangeWalletAction.getValidStrategiesError, payload: result.value };
                    }),
                    catchError((error) => {
                        return observableOf({ type: StocksExchangeWalletAction.getValidStrategiesError, payload: error });
                    }),
                );
            }),
            share(),
        );

    @Effect({ dispatch: false })
    public validStrategiesLoaded$ = this.actions$
    .pipe(
      ofType(StocksExchangeWalletAction.getValidStrategiesSuccess),
      share()
          );

    /**
     * Envia uma estratégia de checkout planejado
     */
    @Effect()
    public sendStrategy$: Observable<StocksExchangeWalletAction> = this.actions$
        .pipe(
			ofType(StocksExchangeWalletAction.sendStrategy),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap((action) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.exchange.routes.getClientStrategies.uri)
                .pipe(
                    map((result: any) => {
                        if (result.ok) {
                            return { type: StocksExchangeWalletAction.sendStrategySuccess, payload: result.value };
                        }
                        return { type: StocksExchangeWalletAction.sendStrategyError, payload: result.value };
                    }),
                    catchError((error) => {
                        return observableOf({ type: StocksExchangeWalletAction.sendStrategyError, payload: error });
                    }),
                );
            }),
            share(),
        );

    /**
     * Busca todas as datas que tiveram movimentações
     */
    @Effect()
    public getTradingDates$: Observable<StocksExchangeWalletAction> = this.actions$
        .pipe(
			ofType(StocksExchangeWalletAction.getTradingDates),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.exchange.routes.tradingDates.uri)
                .pipe(
                    map((result: any) => {
                        if (result.ok) {
                            return { type: StocksExchangeWalletAction.getTradingDatesSuccess, payload: result.value };
                        }
                        return { type: StocksExchangeWalletAction.getTradingDatesError, payload: result.value };
                    }),
                    catchError((error) => {
                        return observableOf({ type: StocksExchangeWalletAction.getTradingDatesError, payload: error });
                    }),
                );
            }),
            share(),
        );

    /**
     * Nova rota que busca todas as datas que tiveram movimentações, ligeiramente diferente da TradingDates
     */
    @Effect()
    public getMyHistoricStrategies$: Observable<StocksExchangeWalletAction> = this.actions$
        .pipe(
			ofType(StocksExchangeWalletAction.getMyHistoricStrategies),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
                return this.backendService
                .get<DefaultResponse>(RoutesTable.composeUri(RoutesTable.exchange.routes.getMyHistoricStrategies, action.payload))
                .pipe(
                    map((result: any) => {
                        if (result.ok) {
                            return { type: StocksExchangeWalletAction.getMyHistoricStrategiesSuccess, payload: result.value };
                        }
                        return { type: StocksExchangeWalletAction.getMyHistoricStrategiesError, payload: result.value };
                    }),
                    catchError((error) => {
                        return observableOf({ type: StocksExchangeWalletAction.getMyHistoricStrategiesError, payload: error });
                    }),
                );
            }),
            share(),
        );

    /**
     * Busca todas as ordens com base em um filtro passado no payload
     */
    @Effect()
    public getOrdersSearch$: Observable<StocksExchangeWalletAction> = this.actions$
        .pipe(
			ofType(StocksExchangeWalletAction.getOrdersSearch),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
                return this.backendService
                    .get<DefaultResponse>(RoutesTable.composeUri(RoutesTable.exchange.routes.getOrdersSearch, action.payload))
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return { type: StocksExchangeWalletAction.getOrdersSearchSuccess, payload: result.value || [] };
                            }
                            return { type: StocksExchangeWalletAction.getOrdersSearchError, payload: result.value };
                        }),
                        catchError((error) => {
                            return observableOf({ type: StocksExchangeWalletAction.getOrdersSearchError, payload: error });
                        }),
                    );
                }),
                share(),
            );

    /**
     * Busca todas as estratégias com base em um filtro passado no payload
     */
    @Effect()
    public getStrategySearch$: Observable<StocksExchangeWalletAction> = this.actions$
        .pipe(
			ofType(StocksExchangeWalletAction.getStretegiesSearch),
            withLatestFrom(this.store.select((s) => s.authentication)),
            filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
            switchMap(([action, auth]) => {
                return this.backendService
                    .get<DefaultResponse>(RoutesTable.composeUri(RoutesTable.exchange.routes.getStrategiesSearch, action.payload))
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return { type: StocksExchangeWalletAction.getStretegiesSearchSuccess, payload: result.value || [] };
                            }
                            return { type: StocksExchangeWalletAction.getStretegiesSearchError, payload: result.value };
                        }),
                        catchError((error) => {
                            return observableOf({ type: StocksExchangeWalletAction.getStretegiesSearchError, payload: error });
                        }),
                    );
            }),
            share(),
        );

    @Effect({ dispatch: false })
    public getExhangeUpdates$ = this.actions$
        .pipe(
            ofType(StocksExchangeWalletAction.getExchangeUpdates),
            withLatestFrom(
                this.store.select((state) => state.authentication.currentUser),
                this.store.select((state) => state.userProfile.user)
            ),
            filter(([action, authenticatedUserData, user]) => {
                const hasSubscribe = this.realtimeService.registerSubscribersAndCheckIfSubscriptionIsNeeded(
                    RealtimeHub.Exchage
                );
                return (!!user && !!authenticatedUserData) && user && authenticatedUserData.cblc && hasSubscribe;
            }),
            map(([action, authenticatedUserData, user]) => {
                return this.realtimeService
                    .subscribeToExchangeUpdates(authenticatedUserData.cblc)
                    .subscribe((update) => {
                        if (update.StrategyID && update.Legs) {
                            if (this.canSendStrategyStatusUpdate(update)) {
                                this.store.dispatch({
                                    type: StocksExchangeWalletAction.strategyUpdated,
                                    payload: this.utilsService.normalize(update),
                                });

                                this.sendLogOfAStrategyEvent("[StrategyStatusUpdatedInMemory]", authenticatedUserData, update);
                            }

                            this.sendLogOfAStrategyEvent("[StrategyStatusReceived]", authenticatedUserData, update);
                        } else if (update.ClOrderID) {
                            this.store.dispatch({
                                type: StocksExchangeWalletAction.orderUpdated,
                                payload: this.utilsService.normalize(update),
                            });

                            this.sendLogOrderReceived(authenticatedUserData, update);
                        } else {
                            this.store.dispatch({
                                type: StocksExchangeWalletAction.custodyUpdated,
                                payload: this.utilsService.normalize(update),
                            });

                            this.sendLogCustodyReceived(authenticatedUserData, update);
                        }
                    });
            }),
            share(),
        );

    @Effect({ dispatch: false })
    public getCustodyUpdates$ = this.actions$
        .pipe(
            ofType(StocksExchangeWalletAction.getCustodyUpdates),
            withLatestFrom(
                this.store.select((state) => state.authentication.currentUser),
                this.store.select((state) => state.userProfile.user)
            ),
            filter(([action, authenticatedUserData, user]) => {
                const hasSubscribe = this.realtimeService.registerSubscribersAndCheckIfSubscriptionIsNeeded(
                    RealtimeHub.Custody
                );
                return (!!user && !!authenticatedUserData) && user && authenticatedUserData.cblc && hasSubscribe;
            }),
            map(([action, authenticatedUserData, user]) => {
                return this.realtimeService.subscribeToCustodyUpdates(authenticatedUserData.cblc)
                    .subscribe((update) => {
                        this.store.dispatch({ type: StocksExchangeWalletAction.custodyUpdated, payload: update });
                    });
            }),
            share(),
        );

    @Effect({ dispatch: false })
    public strategyUpdated$: Observable<StocksExchangeWalletAction> = this.actions$
		.pipe(ofType(StocksExchangeWalletAction.strategyUpdated));


    @Effect({ dispatch: false })
    public orderUpdated$: Observable<StocksExchangeWalletAction> = this.actions$
		.pipe(ofType(StocksExchangeWalletAction.orderUpdated));


    @Effect({ dispatch: false })
    public custodyUpdated$: Observable<StocksExchangeWalletAction> = this.actions$
		.pipe(ofType(StocksExchangeWalletAction.custodyUpdated));


    /**
     * Carregar a lista de investmentos do usuário
     */
    @Effect()
    public getInvestments$ = this.actions$
        .pipe(
			ofType(StocksExchangeWalletAction.getInvestments),
            throttleTime(300),
            exhaustMap(() => {
                return this.backendService.get<IApiQueryListResponse<StocksExchangeInvestment>>(RoutesTable.exchange.routes.getInvestments.uri)
                    .pipe(
                        map((result: any) => {
                            if (result.ok && !result.hasWarning) {
                                // lista de instrumentos
                                this.store.dispatch({
                                    type: StocksMarketAction.getInstrumentsInfoByArraySuccess,
                                    payload: this.extractInstruments(result.value),
                                });
                                // stress
                                this.store.dispatch({
                                    type: PosRiskAction.getStressArraySuccess,
                                    payload: this.extractStresses(result.value),
                                });
                                // distribui os resultados conforme esperados pelos diversos lados da aplicação
                                this.store.dispatch({
                                    type: StocksExchangeWalletAction.getValidStrategiesSuccess,
                                    payload: this.extractStrategies(result.value),
                                });
                                this.store.dispatch({
                                    type: StocksExchangeWalletAction.getValidOrdersSuccess,
                                    payload: this.extractOrders(result.value),
                                });
                                this.store.dispatch({
                                    type: PosRiskAction.getClientCustodiesSuccess,
                                    payload: this.extractCustodies(result.value),
                                });
                                this.store.dispatch({
                                    type: StructuredProductsAction.getClientCustodiesSuccess,
                                    payload: this.extractStructuredProducts(result.value),
                                });
                                return {type: StocksExchangeWalletAction.getInvestmentsSuccess, payload: result.value };
                            }
                            return {type: StocksExchangeWalletAction.getInvestmentsError, payload: result.messages};
                        }),
                        catchError((error) => {
                            return observableOf({type: StocksExchangeWalletAction.getInvestmentsError, payload: error});
                        }),
                    );
            }),
            share(),
        );

    public strategiesWithFinalStatus = new Object();

    constructor(
        private actions$: Actions<StocksExchangeWalletAction>,
        private backendService: BackendService,
        private store: Store<AppState>,
        private realtimeService: RealtimeService,
        private parserService: BackendToFrontendParserService,
        private utilsService: UtilitiesService,
        private sentryService: SentryService
    ) {}

    public sendLogOfAStrategyEvent(eventName, authenticatedUserData, update) {
        this.sentryService.sendEvent({
            message: eventName,
            tags: {
                'cblc': authenticatedUserData.cblc,
                'stockName': update.Symbol,
                'strategyId': update.StrategyID,
                'strategyStatus': update.Status
            },
            extra: {
                'cblc': authenticatedUserData.cblc,
                'strategy': update,
                'legs': update.Legs
            }
        });
    }

    public sendLogOrderReceived(authenticatedUserData, update) {
        this.sentryService.sendEvent({
            message: "[OrderStatusUpdated]",
            tags: {
                'cblc': authenticatedUserData.cblc,
                'stockName': update.Symbol,
                'orderId': update.ID,
                'orderStatus': update.Status
            },
            extra: {
                'cblc': authenticatedUserData.cblc,
                'order': update
            }
        });
    }

    public sendLogCustodyReceived(authenticatedUserData, update) {
        this.sentryService.sendEvent({
            message: '[CustodyUpdate]',
            tags: {
                'cblc': authenticatedUserData.cblc
            },
            extra: {
                'cblc': authenticatedUserData.cblc,
                'custody': update
            }
        });
    }

    public isStrategyWithFinalStatus(oldStrategyData) {
        return (
            oldStrategyData.Status == ExchangeOrderStatus.Filled ||
            oldStrategyData.Status == ExchangeOrderStatus.Expired ||
            oldStrategyData.Status == ExchangeOrderStatus.CanceledByEvent ||
            oldStrategyData.Status == ExchangeOrderStatus.Cancelled ||
            oldStrategyData.Status == ExchangeOrderStatus.Rejected ||
            oldStrategyData.Status == ExchangeOrderStatus.Suspendend ||
            oldStrategyData.Status == ExchangeOrderStatus.CanceledByEndDayRoutine ||
            oldStrategyData.Status == ExchangeOrderStatus.CanceledByShutdownStrategy
        );
    }

    public canSendStrategyStatusUpdate(strategyReceived) {
        const wasStrategyReceivedSavedWithFinalStatus = this.strategiesWithFinalStatus[strategyReceived.StrategyID];

        if (!wasStrategyReceivedSavedWithFinalStatus 
            && this.isStrategyWithFinalStatus(strategyReceived)) {
            this.saveInStrategiesWithFinalStatus(strategyReceived);
        }

        return !wasStrategyReceivedSavedWithFinalStatus;
    }

    public saveInStrategiesWithFinalStatus(strategyReceived) {
        this.strategiesWithFinalStatus[strategyReceived.StrategyID] = strategyReceived;
    }

    /**
     * Extrai informações do stress do ativo a partir de uma lista de investimentos
     * @param investments lista de investimentos
     */
    private extractStresses(investments: StocksExchangeInvestment[]): Stress[] {
        return investments
            .map((i) => new Stress({...i.instrument.stress, ...{instrument: i.symbol}}));
    }
    /**
     * Realiza o parse entre o objeto que recebemos da nova rota única na api para um que o código espera receber (estratégia)
     * @param investments lista de investimentos
     */
    private extractStrategies(investments: StocksExchangeInvestment[]): Strategy[] {
        return investments
            .filter((i) => i.planned && i.investmentType !== InvestmentTypeEnum.StructuredProducts)
            .map((i) => this.parserService.parseInvestmentToStrategy(i));
    }

    /**
     * Realiza o parse entre o objeto que recebemos da nova rota única na api para um que o código espera receber (ordem)
     * @param investments lista de investimentos
     */
    private extractOrders(investments: StocksExchangeInvestment[]): Order[] {
        return investments
            .filter((i) => i.planned !== true && i.id && i.investmentType !== InvestmentTypeEnum.StructuredProducts)
            .map((order) => this.parserService.parseInvestmentToOrder(order));
    }

    /**
     * Realiza o parse entre o objeto que recebemos da nova rota única na api para um que o código espera receber (custódia)
     * @param investments lista de investimentos
     */
    private extractCustodies(investments: StocksExchangeInvestment[] ): CustodyClientOrder[] {
        return investments
            .filter((i) => i.planned !== true && !i.id && i.investmentType !== InvestmentTypeEnum.StructuredProducts)
            .map((order) => this.parserService.parseInvestmentToCustory(order));
    }

    /**
     * Realiza o parse entre o objeto que recebemos da nova rota única na api para um que o código espera receber (custódia)
     * @param investments lista de investimentos
     */
        private extractStructuredProducts(investments: StocksExchangeInvestment[] ): CustodyStructuredProduct[] {
            return investments
                .filter((i) => i.planned !== true && !i.id && i.investmentType === InvestmentTypeEnum.StructuredProducts)
                .map((order) => this.parserService.parseInvestmentToStructuredCustody(order));
        }

    /**
     * Extrai uma lista de instrumentos da lista de investimentos
     * @param investments lista de investimentos a ser usada
     */
    private extractInstruments(investments: StocksExchangeInvestment[] ): Instrument[] {
        return investments
            .map((i) => {
                const instrument = new Instrument();
                const instrumentLite = new InstrumentLite(i.instrument);
                instrument.fromInstrumentLite(instrumentLite);
                return instrument;
            });
    }
}
